// src/components/FollowButton.jsx
// Reusable follow/unfollow button.
// Handles optimistic UI, loading state, and auth gate.

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { followArtist, unfollowArtist, isFollowing } from '../firebase-utils';

export default function FollowButton({
  artistId,
  artistName = '',
  onAuthRequired,      // called when user isn't signed in
  size = 'md',         // 'sm' | 'md' | 'lg'
  showCount = false,   // show follower count next to button
  initialCount = 0,    // gigwaveFollowerCount from profile
  className = '',
}) {
  const { currentUser } = useAuth();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(Number(initialCount) || 0);

  // Check initial follow state
  useEffect(() => {
    if (!currentUser || !artistId) { setLoading(false); return; }
    // Don't show follow button for your own profile
    if (currentUser.uid === artistId) { setLoading(false); return; }

    isFollowing(artistId, currentUser.uid)
      .then(result => { setFollowing(result); setLoading(false); })
      .catch(() => setLoading(false));
  }, [artistId, currentUser]);

  // Keep count in sync if prop changes
  useEffect(() => {
    setCount(Number(initialCount) || 0);
  }, [initialCount]);

  // Don't render for own profile
  if (currentUser?.uid === artistId) return null;

  const handleClick = async (e) => {
    e.stopPropagation();

    if (!currentUser) {
      if (onAuthRequired) onAuthRequired();
      return;
    }

    // Optimistic update
    const wasFollowing = following;
    setFollowing(!wasFollowing);
    setCount(prev => wasFollowing ? Math.max(0, prev - 1) : prev + 1);
    setLoading(true);

    try {
      if (wasFollowing) {
        await unfollowArtist(artistId, currentUser.uid);
      } else {
        await followArtist(artistId, currentUser.uid, currentUser.displayName || currentUser.email);
      }
    } catch (err) {
      // Revert on error
      setFollowing(wasFollowing);
      setCount(prev => wasFollowing ? prev + 1 : Math.max(0, prev - 1));
      console.error('Follow error:', err);
    } finally {
      setLoading(false);
    }
  };

  const sizeStyles = {
    sm: { padding: '4px 10px', fontSize: '12px' },
    md: { padding: '8px 16px', fontSize: '14px' },
    lg: { padding: '10px 22px', fontSize: '15px' },
  };

  const style = {
    ...sizeStyles[size] || sizeStyles.md,
    borderRadius: '50px',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.18s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    opacity: loading ? 0.7 : 1,
    border: following
      ? '1px solid rgba(168,85,247,0.5)'
      : '1px solid rgba(0,212,255,0.5)',
    background: following
      ? 'rgba(168,85,247,0.15)'
      : 'rgba(0,212,255,0.12)',
    color: following ? '#c084fc' : '#00d4ff',
  };

  return (
    <button onClick={handleClick} style={style} className={className} disabled={loading}>
      <span style={{ fontSize: size === 'sm' ? '12px' : '14px' }}>
        {following ? '✓' : '+'}
      </span>
      <span>{following ? 'Following' : 'Follow'}</span>
      {showCount && count > 0 && (
        <span style={{
          fontSize: '11px',
          padding: '1px 6px',
          borderRadius: '20px',
          background: following ? 'rgba(168,85,247,0.25)' : 'rgba(0,212,255,0.2)',
          color: 'inherit',
        }}>
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}
